import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authAxios } from "setups/axios";

/** Support multiple backend envelope shapes for GET /api/v1/user */
function extractUserFromProfileResponse(res) {
  const root = res?.data;
  if (!root || typeof root !== "object") return null;

  const d = root.data;
  if (d != null && typeof d === "object" && !Array.isArray(d)) {
    if (d._id || d.id || d.SAP_ID) return d;
    if (d.user && typeof d.user === "object") return d.user;
  }
  if (root.user && typeof root.user === "object") return root.user;
  if (root._id || root.id || root.SAP_ID) return root;

  return null;
}

export const fetchUser = createAsyncThunk("user/fetchUser", async () => {
  const token = localStorage.getItem("token");

  if (!token) {
    console.info("[user/fetchUser] skipped — no token in localStorage");
    return {};
  }

  try {
    const res = await authAxios()({
      url: `/api/v1/user`,
      method: "GET",
    });

    const data = extractUserFromProfileResponse(res) ?? res?.data?.data ?? null;
    
    if (data) {
      const zone = data.zone || (data.scope && data.scope.zones && data.scope.zones.length > 0 ? data.scope.zones.join(",") : null);
      if (zone) {
        localStorage.setItem("admin_zone", zone);
      }
    }

    console.info("[user/fetchUser] GET /api/v1/user ok", {
      status: res?.status,
      hasData: Boolean(data),
      keys:
        data && typeof data === "object" && !Array.isArray(data)
          ? Object.keys(data).slice(0, 30)
          : [],
    });

    return data ?? {};
  } catch (err) {
    console.error("[user/fetchUser] GET /api/v1/user failed", {
      message: err?.message,
      status: err?.response?.status,
      responseData: err?.response?.data,
      baseURL: process.env.REACT_APP_REQUEST_ENDPOINT,
    });
    throw err;
  }
});

export const userSlice = createSlice({
  name: "user",
  // If a token already exists we are about to fire fetchUser — start in
  // loading=true so route guards show a spinner instead of the login page.
  initialState: {
    loading: !!localStorage.getItem("token"),
    user: {},
  },
  reducers: {
    bootstrapFromSso: (state, action) => {
      const p = action.payload ?? {};
      state.loading = false;
      state.user = {
        ...p,
        _id: p._id ?? p.id,
      };
    },
  },
  extraReducers: {
    [fetchUser.fulfilled]: (state, action) => {
      const raw = action.payload ?? {};
      const user =
        raw && typeof raw === "object"
          ? { ...raw, _id: raw._id ?? raw.id }
          : {};

      const hasIncomingId = Boolean(user._id);
      if (!hasIncomingId && state.user?._id) {
        state.loading = false;
        console.info(
          "[user/fetchUser] fulfilled empty payload — keeping existing user in store"
        );
        return;
      }

      console.info("[user/fetchUser] fulfilled → store", {
        _id: user._id,
        hadRawId: Boolean(raw?.id),
        hadRaw_id: Boolean(raw?._id),
      });

      state.loading = false;
      state.user = user;
    },
    [fetchUser.rejected]: (state, action) => {
      state.loading = false;
      const arg = action.meta?.arg;
      const preserve =
        arg && typeof arg === "object" && arg.preserveUser === true;
      if (!preserve) {
        state.user = {};
      }
    },
    [fetchUser.pending]: (state, action) => {
      state.loading = true;
      const arg = action.meta?.arg;
      const preserve =
        arg && typeof arg === "object" && arg.preserveUser === true;
      if (!preserve) {
        state.user = {};
      }
    },
  },
});

export const { bootstrapFromSso } = userSlice.actions;

export default userSlice.reducer;
